// Local Storage buffer for unsynced video watch time.
//
// One key per play session - lms.watch.v1.<studentId>.<sessionId> - so each tab only ever
// writes its own sessions (no read-modify-write races between tabs). Nothing here touches
// the auth keys (authToken / expirationTime / studentId / logout-event) and the buffer
// deliberately SURVIVES logout: a token that expires mid-video logs the student out (15 min
// JWT), and the unsynced time must still reach the server on the next login.
// Every storage access is wrapped: localStorage can throw (private mode, quota, blocked).

export const WATCH_KEY_PREFIX = "lms.watch.v1.";
const MAX_AGE_MS = 30 * 24 * 60 * 60 * 1000;

// Each live page refreshes a small heartbeat key; an open entry whose owning page has no fresh
// heartbeat belongs to a closed / crashed page and may be finished off by any other page.
export const TAB_KEY_PREFIX = "lms.watchtab.v1.";
export const HEARTBEAT_FRESH_MS = 30 * 1000;

export const entryKey = (studentId, sessionId) =>
	`${WATCH_KEY_PREFIX}${studentId}.${sessionId}`;

export function saveEntry(entry) {
	try {
		localStorage.setItem(
			entryKey(entry.studentId, entry.sessionId),
			JSON.stringify(entry)
		);
		return true;
	} catch (error) {
		return false;
	}
}

export function readEntry(studentId, sessionId) {
	try {
		const raw = localStorage.getItem(entryKey(studentId, sessionId));
		return raw ? JSON.parse(raw) : null;
	} catch (error) {
		return null;
	}
}

export function removeEntry(studentId, sessionId) {
	try {
		localStorage.removeItem(entryKey(studentId, sessionId));
	} catch (error) {
		// nothing to clean up
	}
}

// All buffered entries, optionally only those of one student. Corrupt values are skipped.
export function listEntries(studentId = null) {
	const entries = [];
	try {
		const prefix =
			studentId === null
				? WATCH_KEY_PREFIX
				: `${WATCH_KEY_PREFIX}${studentId}.`;
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key || !key.startsWith(prefix)) continue;
			try {
				const entry = JSON.parse(localStorage.getItem(key));
				if (entry && entry.sessionId && entry.studentId !== undefined) {
					entries.push(entry);
				}
			} catch (error) {
				// corrupt entry: ignored (purgeExpired removes it eventually)
			}
		}
	} catch (error) {
		return [];
	}
	return entries;
}

// Drops entries older than 30 days and unreadable ones (shared-computer hygiene).
export function purgeExpired(now = Date.now()) {
	try {
		const doomed = [];
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (!key || !key.startsWith(WATCH_KEY_PREFIX)) continue;
			try {
				const entry = JSON.parse(localStorage.getItem(key));
				const started = Date.parse(entry && entry.startedAt);
				if (!Number.isFinite(started) || now - started > MAX_AGE_MS) {
					doomed.push(key);
				}
			} catch (error) {
				doomed.push(key);
			}
		}
		for (let i = 0; i < localStorage.length; i++) {
			const key = localStorage.key(i);
			if (
				key &&
				key.startsWith(TAB_KEY_PREFIX) &&
				now - Number(localStorage.getItem(key)) > 24 * 60 * 60 * 1000
			) {
				doomed.push(key);
			}
		}
		doomed.forEach((key) => localStorage.removeItem(key));
	} catch (error) {
		// storage unavailable
	}
}

export function touchTab(tabId, now = Date.now()) {
	try {
		localStorage.setItem(TAB_KEY_PREFIX + tabId, String(now));
	} catch (error) {
		// storage unavailable
	}
}

export function clearTab(tabId) {
	try {
		localStorage.removeItem(TAB_KEY_PREFIX + tabId);
	} catch (error) {
		// storage unavailable
	}
}

export function isTabAlive(tabId, now = Date.now()) {
	try {
		const beat = Number(localStorage.getItem(TAB_KEY_PREFIX + tabId));
		return Number.isFinite(beat) && beat > 0 && now - beat < HEARTBEAT_FRESH_MS;
	} catch (error) {
		return false;
	}
}

export function newSessionId() {
	if (typeof crypto !== "undefined" && crypto.randomUUID) {
		return crypto.randomUUID();
	}
	// fallback for older browsers / insecure contexts
	return "xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx".replace(/[xy]/g, (c) => {
		const r = (Math.random() * 16) | 0;
		return (c === "x" ? r : (r & 0x3) | 0x8).toString(16);
	});
}
